/**
 * /payroll-l3/apprentice-compliance — Apprentice ratio dashboard.
 *
 * Wired to live endpoints:
 *   GET  /api/payroll-l3/apprentice-ratios         list ratios (with filters)
 *   POST /api/payroll-l3/apprentice-ratios/recompute  recompute for a date range
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l3/apprentice-compliance")({
  component: ApprenticeCompliancePage,
});

interface Local {
  id: string;
  name: string;
}

interface RatioRow {
  id: string;
  project_id: string;
  local_id: string;
  period_start: string;
  period_end: string;
  journeyman_hours: string;
  apprentice_hours: string;
  required_ratio: string | null;
  actual_ratio: string | null;
  compliant: boolean | null;
  computed_at: string | null;
}

interface RecomputeResponse {
  count: number;
  compliant: number;
  non_compliant: number;
  unknown: number;
}

function complianceChip(compliant: boolean | null) {
  if (compliant === true) return <Chip label="Compliant" size="small" color="success" />;
  if (compliant === false) return <Chip label="Violation" size="small" color="error" />;
  return <Chip label="Unknown" size="small" color="default" />;
}

function fmtRatio(v: string | null): string {
  if (!v) return "—";
  const n = Number(v);
  if (isNaN(n)) return v;
  // e.g. 0.333 → "3:1 (33%)"  or just show as decimal
  return `${n.toFixed(3)}`;
}

// ─── Recompute dialog ─────────────────────────────────────────────────────────

function RecomputeDialog({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: (res: RecomputeResponse) => void;
}) {
  const [period_start, setPeriodStart] = useState("");
  const [period_end, setPeriodEnd] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api<RecomputeResponse>("/api/payroll-l3/apprentice-ratios/recompute", {
        method: "POST",
        body: { period_start, period_end },
      }),
    onSuccess: (res) => {
      onDone(res);
      onClose();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Recompute failed"),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <span>Recompute Ratios</span>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mt={0.5}>
          {err && <Alert severity="error">{err}</Alert>}
          <TextField
            type="date" label="Period Start *" size="small" fullWidth
            InputLabelProps={{ shrink: true }}
            value={period_start}
            onChange={(e) => setPeriodStart(e.target.value)}
          />
          <TextField
            type="date" label="Period End *" size="small" fullWidth
            InputLabelProps={{ shrink: true }}
            value={period_end}
            onChange={(e) => setPeriodEnd(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!period_start || !period_end || mut.isPending}
          onClick={() => { setErr(null); mut.mutate(); }}
        >
          {mut.isPending ? "Computing…" : "Recompute"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function ApprenticeCompliancePage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({
    compliant: "" as "" | "true" | "false",
    local_id: "",
    period_start: "",
    period_end: "",
  });
  const [recomputeOpen, setRecomputeOpen] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState<RecomputeResponse | null>(null);

  const localsQ = useQuery({
    queryKey: ["payroll-l3", "locals"],
    queryFn: () => api<Local[]>("/api/payroll-l3/locals"),
  });

  const params = new URLSearchParams();
  if (filters.compliant !== "") params.set("compliant", filters.compliant);
  if (filters.local_id) params.set("local_id", filters.local_id);
  if (filters.period_start) params.set("period_start", filters.period_start);
  if (filters.period_end) params.set("period_end", filters.period_end);
  const qs = params.toString();

  const ratiosQ = useQuery({
    queryKey: ["payroll-l3", "apprentice-ratios", filters],
    queryFn: () =>
      api<RatioRow[]>(`/api/payroll-l3/apprentice-ratios${qs ? `?${qs}` : ""}`),
  });

  const localName = (id: string) =>
    localsQ.data?.find((l) => l.id === id)?.name ?? id.slice(0, 8) + "…";

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>Apprentice Ratio Compliance</Typography>
        <Stack direction="row" alignItems="center" gap={2}>
          <HelpHint docPath="payroll-l3/apprentice-ratios.md" label="Apprentice ratios help" />
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={() => setRecomputeOpen(true)}
          >
            Recompute
          </Button>
        </Stack>
      </Stack>

      {recomputeResult && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setRecomputeResult(null)}
        >
          Recomputed {recomputeResult.count} ratios — {recomputeResult.compliant} compliant,{" "}
          {recomputeResult.non_compliant} violations, {recomputeResult.unknown} unknown.
        </Alert>
      )}

      <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
        <TextField
          select size="small" label="Status"
          value={filters.compliant}
          onChange={(e) => setFilters((p) => ({ ...p, compliant: e.target.value as "" | "true" | "false" }))}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Compliant</MenuItem>
          <MenuItem value="false">Violation</MenuItem>
        </TextField>
        <TextField
          select size="small" label="Local" value={filters.local_id}
          onChange={(e) => setFilters((p) => ({ ...p, local_id: e.target.value }))}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Locals</MenuItem>
          {(localsQ.data ?? []).map((l) => (
            <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
          ))}
        </TextField>
        <TextField
          type="date" size="small" label="Period Start ≥"
          value={filters.period_start}
          onChange={(e) => setFilters((p) => ({ ...p, period_start: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date" size="small" label="Period End ≤"
          value={filters.period_end}
          onChange={(e) => setFilters((p) => ({ ...p, period_end: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      {ratiosQ.isLoading && <CircularProgress size={24} />}
      {ratiosQ.error && <Alert severity="error">Failed to load apprentice ratios.</Alert>}

      {ratiosQ.data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Local</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">JW Hours</TableCell>
              <TableCell align="right">App Hours</TableCell>
              <TableCell align="right">Actual Ratio</TableCell>
              <TableCell align="right">Required Ratio</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Computed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ratiosQ.data.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  {r.project_id.slice(0, 8)}…
                </TableCell>
                <TableCell>{localName(r.local_id)}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>
                  {new Date(r.period_start).toLocaleDateString()} →{" "}
                  {new Date(r.period_end).toLocaleDateString()}
                </TableCell>
                <TableCell align="right">{Number(r.journeyman_hours).toFixed(1)}</TableCell>
                <TableCell align="right">{Number(r.apprentice_hours).toFixed(1)}</TableCell>
                <TableCell align="right">{fmtRatio(r.actual_ratio)}</TableCell>
                <TableCell align="right">{fmtRatio(r.required_ratio)}</TableCell>
                <TableCell>{complianceChip(r.compliant)}</TableCell>
                <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>
                  {r.computed_at ? new Date(r.computed_at).toLocaleDateString() : "—"}
                </TableCell>
              </TableRow>
            ))}
            {ratiosQ.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  No ratio records — use Recompute to generate them for a date range.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <RecomputeDialog
        open={recomputeOpen}
        onClose={() => setRecomputeOpen(false)}
        onDone={(res) => {
          setRecomputeResult(res);
          qc.invalidateQueries({ queryKey: ["payroll-l3", "apprentice-ratios"] });
        }}
      />
    </Box>
  );
}
